import { APP_NAME } from '@shared/constants.js';
import { countryCodeToFlag } from '../utils/helpers.js';
import { favoritesManager } from '../services/FavoritesManager.js';

/**
 * AudioPlayer — City-based radio player (Radio Garden style)
 *
 * Features:
 * - City-based navigation: ⏮⏭ cycle through stations within a city
 * - Play/pause radio streams via HTML5 <audio>
 * - Background audio on mobile (screen locked) via Media Session API
 * - Lock screen controls (play, pause, next, previous)
 * - Volume control
 * - Badge showing position (e.g., "2/5")
 */
export class AudioPlayer {
  constructor({ audioEl, playerEl }) {
    this.audio = audioEl;
    this.playerEl = playerEl;

    // City-based state
    this.currentCity = null;          // CityPoint object
    this.currentStation = null;       // Current station within city
    this.cityStationIndex = 0;        // Index within city.stations[]

    this.isPlaying = false;
    this.isUnlocked = false;
    this.pendingCity = null;
    this.cityHistory = new Map();  // Tracks last played index per city

    // DOM refs
    this.playBtn = document.getElementById('player-play');
    this.btnIcon = document.getElementById('player-btn-icon');
    this.nameEl = document.getElementById('player-name');
    this.locationEl = document.getElementById('player-location');
    this.volumeEl = document.getElementById('player-volume');
    this.favBtn = document.getElementById('player-fav');
    this.prevBtn = document.getElementById('player-prev');
    this.nextBtn = document.getElementById('player-next');
    this.badgeEl = document.getElementById('player-city-badge');
    this.eqEl = document.getElementById('player-equalizer');

    this._setupEventListeners();
    this._setupMediaSession();
    this._setupAudioUnlock();
    this._setupFavoriteButton();
  }

  /**
   * Unlock audio on first user interaction.
   */
  _setupAudioUnlock() {
    const unlock = () => {
      if (this.isUnlocked) return;
      this.isUnlocked = true;
      console.log('🔓 Audio unlocked by user interaction');

      if (this.pendingCity) {
        this.tuneCity(this.pendingCity);
        this.pendingCity = null;
      }

      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('keydown', unlock);
    };

    document.addEventListener('click', unlock, { once: false });
    document.addEventListener('touchstart', unlock, { once: false });
    document.addEventListener('keydown', unlock, { once: false });
  }

  /**
   * Tune to a city — auto-advances to next station on revisit.
   */
  tuneCity(cityPoint, stationIndex = null) {
    if (!cityPoint?.stations?.length) return;

    // If no explicit index, check history and advance
    if (stationIndex === null) {
      const lastIndex = this.cityHistory.get(cityPoint.id);
      if (lastIndex !== undefined && cityPoint.stations.length > 1) {
        // Advance to next station
        stationIndex = (lastIndex + 1) % cityPoint.stations.length;
      } else {
        stationIndex = 0;
      }
    }

    this.currentCity = cityPoint;
    this.cityStationIndex = stationIndex;
    this.cityHistory.set(cityPoint.id, stationIndex);

    const station = cityPoint.stations[stationIndex];
    this._playStation(station);
    this._updateNavButtons();
  }

  /**
   * Play a specific station (internal)
   */
  _playStation(station) {
    if (!station?.stream_url) return;

    this.currentStation = station;
    this._updateUI(false);
    this._updateMediaSessionMetadata(station);
    this._updateFavButton();
    this._showPlayer();

    if (!this.isUnlocked) {
      this.pendingCity = this.currentCity;
      console.log('[AudioPlayer] Waiting for user interaction to play:', station.name);
      return;
    }

    this.audio.src = station.stream_url;
    this.audio.load();

    this.audio.play()
      .then(() => {
        console.log('▶ Playing:', station.name);
        this._updateUI(true);
      })
      .catch(err => {
        console.warn('[AudioPlayer] Play failed:', err.message);
        this._updateUI(false);
      });
  }

  /**
   * Legacy: play(station) — wraps in a single-station city
   */
  play(station) {
    if (this.currentCity && this.currentCity.stations.includes(station)) {
      const idx = this.currentCity.stations.indexOf(station);
      this.tuneCity(this.currentCity, idx);
    } else {
      // Wrap in a virtual city for backward compat
      const virtualCity = {
        id: `virtual-${station.id}`,
        city: station.city || 'Unknown',
        country_code: station.country_code,
        country_name: station.country_name,
        latitude: station.latitude,
        longitude: station.longitude,
        stationCount: 1,
        stations: [station],
      };
      this.tuneCity(virtualCity, 0);
    }
  }

  /**
   * Toggle play/pause
   */
  toggle() {
    if (!this.currentStation) return;

    if (this.audio.paused) {
      this.audio.play().catch(() => {});
      this._updateUI(true);
    } else {
      this.audio.pause();
      this._updateUI(false);
    }
  }

  /**
   * Play next station WITHIN the current city
   */
  playNext() {
    if (!this.currentCity?.stations?.length) return;
    const count = this.currentCity.stations.length;
    if (count <= 1) return; // Only 1 station, can't skip

    this.cityStationIndex = (this.cityStationIndex + 1) % count;
    this.cityHistory.set(this.currentCity.id, this.cityStationIndex);
    this._playStation(this.currentCity.stations[this.cityStationIndex]);
    this._updateNavButtons();
    console.log(`⏭ Next: ${this.currentStation.name} (${this.cityStationIndex + 1}/${count})`);
  }

  /**
   * Play previous station WITHIN the current city
   */
  playPrevious() {
    if (!this.currentCity?.stations?.length) return;
    const count = this.currentCity.stations.length;
    if (count <= 1) return;

    this.cityStationIndex = (this.cityStationIndex - 1 + count) % count;
    this.cityHistory.set(this.currentCity.id, this.cityStationIndex);
    this._playStation(this.currentCity.stations[this.cityStationIndex]);
    this._updateNavButtons();
    console.log(`⏮ Prev: ${this.currentStation.name} (${this.cityStationIndex + 1}/${count})`);
  }

  /**
   * Set volume (0-100)
   */
  setVolume(vol) {
    this.audio.volume = Math.max(0, Math.min(1, vol / 100));
  }

  /**
   * Stop playback
   */
  stop() {
    this.audio.pause();
    this.audio.src = '';
    this.currentStation = null;
    this.currentCity = null;
    this.isPlaying = false;
    this._updateUI(false);
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  _setupEventListeners() {
    this.playBtn.addEventListener('click', () => this.toggle());

    // Volume slider
    this.volumeEl.addEventListener('input', (e) => {
      this.setVolume(parseInt(e.target.value));
    });

    // ⏮ ⏭ navigation
    this.prevBtn?.addEventListener('click', () => this.playPrevious());
    this.nextBtn?.addEventListener('click', () => this.playNext());

    // Audio events
    this.audio.addEventListener('playing', () => {
      this.isPlaying = true;
      this._updateUI(true);
      this.playerEl.classList.remove('buffering');
    });

    this.audio.addEventListener('pause', () => {
      this.isPlaying = false;
      this._updateUI(false);
    });

    this.audio.addEventListener('waiting', () => {
      this.playerEl.classList.add('buffering');
    });

    this.audio.addEventListener('error', (e) => {
      console.error('[AudioPlayer] Stream error:', e);
      this.playerEl.classList.remove('buffering');
      this._updateUI(false);
    });

    this.setVolume(parseInt(this.volumeEl.value));
  }

  _setupMediaSession() {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.setActionHandler('play', () => {
      this.audio.play().catch(() => {});
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      this.audio.pause();
    });

    navigator.mediaSession.setActionHandler('nexttrack', () => {
      this.playNext();
    });

    navigator.mediaSession.setActionHandler('previoustrack', () => {
      this.playPrevious();
    });
  }

  _updateMediaSessionMetadata(station) {
    if (!('mediaSession' in navigator)) return;

    const flag = countryCodeToFlag(station.country_code);
    navigator.mediaSession.metadata = new MediaMetadata({
      title: station.name,
      artist: station.city
        ? `${flag} ${station.city}, ${station.country_name}`
        : `${flag} ${station.country_name}`,
      album: APP_NAME,
      artwork: [
        {
          src: station.logo_url || '/icons/icon-192.png',
          sizes: '192x192',
          type: 'image/png',
        },
        {
          src: station.logo_url || '/icons/icon-512.png',
          sizes: '512x512',
          type: 'image/png',
        },
      ],
    });
  }

  _showPlayer() {
    this.playerEl.classList.remove('hidden');
  }

  _updateUI(playing) {
    this.isPlaying = playing;
    this.btnIcon.innerHTML = playing
      ? '<rect x="5" y="3" width="4" height="18" rx="1"/><rect x="15" y="3" width="4" height="18" rx="1"/>'
      : '<polygon points="6,3 20,12 6,21"/>';

    // Toggle equalizer animation
    if (this.eqEl) {
      this.eqEl.classList.toggle('playing', playing);
    }

    if (this.currentStation) {
      const flag = countryCodeToFlag(this.currentStation.country_code);
      this.nameEl.textContent = this.currentStation.name;
      this.locationEl.textContent = this.currentStation.city
        ? `${flag} ${this.currentStation.city}, ${(this.currentStation.country_code || '').toUpperCase()}`
        : `${flag} ${(this.currentStation.country_code || '').toUpperCase()}`;
    }

    // Update badge
    this._updateBadge();

    // Notify mobile UI
    window.dispatchEvent(new CustomEvent('player-state-change', { detail: { playing } }));
  }

  _updateBadge() {
    if (!this.badgeEl || !this.currentCity) return;
    const count = this.currentCity.stations.length;
    if (count > 1) {
      this.badgeEl.textContent = `${this.cityStationIndex + 1}/${count}`;
      this.badgeEl.classList.remove('hidden');
    } else {
      this.badgeEl.classList.add('hidden');
    }
  }

  _updateNavButtons() {
    const count = this.currentCity?.stations?.length || 0;
    const disabled = count <= 1;

    if (this.prevBtn) {
      this.prevBtn.disabled = disabled;
      this.prevBtn.classList.toggle('disabled', disabled);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = disabled;
      this.nextBtn.classList.toggle('disabled', disabled);
    }
  }

  _setupFavoriteButton() {
    if (!this.favBtn) return;

    this.favBtn.addEventListener('click', () => {
      if (!this.currentStation) return;
      favoritesManager.toggle(this.currentStation.id);
      this._updateFavButton();
    });
  }

  _updateFavButton() {
    if (!this.favBtn || !this.currentStation) return;
    const isFav = favoritesManager.isFavorite(this.currentStation.id);
    this.favBtn.dataset.stationId = String(this.currentStation.id);
    this.favBtn.classList.toggle('favorited', isFav);
  }
}
